/**
 * LICITARS 3.0 - Document Select V3
 * Simplified version for Chat8HeaderV3 without preview card
 */

import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { logger } from "@/utils/logger";
import { useDebounce } from "@/hooks/useDebounce";
import { Loader2 } from "lucide-react";

interface DocumentSelectV3Props {
  documents: Array<{
    id: string;
    name?: string;
    title?: string;
    description?: string;
    document_type?: {
      id: string | number;
      name: string;
    };
  }>;
  selectedDocumentId?: string | null;
  onSelect: (documentId: string) => void;
  disabled?: boolean;
  className?: string;
  showPreview?: boolean;
}

export function DocumentSelectV3({
  documents = [],
  selectedDocumentId,
  onSelect,
  disabled = false,
  className = "",
  showPreview = false,
}: DocumentSelectV3Props) {
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(
    selectedDocumentId || null,
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const debouncedOnSelect = useDebounce(onSelect, 300);
  // Sync local state with prop changes
  useEffect(() => {
    if (localSelectedId !== selectedDocumentId) {
      setLocalSelectedId(selectedDocumentId);
    }
  }, [selectedDocumentId, localSelectedId]);
  // Filter out documents with invalid data and remove duplicates
  const validDocuments = documents
    .filter((doc) => {
      const isValid =
        doc &&
        doc.id &&
        typeof doc.id === "string" &&
        doc.id.trim() !== "" &&
        (doc.name || doc.title);

      if (!isValid) {
        logger.error("[DocumentSelectV3] Invalid document:", {
          id: doc?.id,
          name: doc?.name,
          title: doc?.title,
        });
      }
      return isValid;
    })
    .filter(
      (doc, index, self) =>
        index === self.findIndex((d) => d.id === doc.id),
    );
  const selectedDocument = validDocuments.find(
    (doc) => doc.id === localSelectedId,
  );
  const handleSelect = (value: string) => {
    logger.info(
      `[DocumentSelectV3] Usuário selecionou documento: ${value}`,
    );
    setIsSelecting(true);
    setLocalSelectedId(value);
    debouncedOnSelect(value);
    setTimeout(() => setIsSelecting(false), 300);
  };
  return (
    <div className={`flex-1 min-w-0 ${className}`}>
      <Select
        value={localSelectedId || ""}
        onValueChange={handleSelect}
        disabled={disabled || isSelecting}
      >
        <SelectTrigger
          className="w-full bg-transparent border-white/10 text-white hover:bg-white/5 transition-colors focus:ring-white/20"
        >
          <SelectValue
            placeholder="Selecione um documento"
            className="text-white"
          />
        </SelectTrigger>
        <SelectContent className="bg-navy-700 border-navy text-platinum">
          {validDocuments.length === 0 ? (
            <SelectItem
              value="none"
              disabled
              className="text-silver/50"
            >
              Nenhum documento disponível
            </SelectItem>
          ) : (
            validDocuments.map((doc) => {
              const displayName =
                doc.name || doc.title || "Documento sem título";
              return (
                <SelectItem
                  key={doc.id}
                  value={doc.id}
                  className="hover:bg-navy-600 focus:bg-navy-600 text-platinum cursor-pointer"
                >
                  {displayName}
                </SelectItem>
              );
            })
          )}
        </SelectContent>
      </Select>
      {isSelecting && (
        <div className="flex items-center gap-2 mt-1 text-xs text-silver/70">
          <Loader2 className="h-3 w-3 animate-spin" />
          Carregando...
        </div>
      )}
    </div>
  );
}
